export const AGENT_TYPES = Object.freeze({
  COMPS: "comps",
  QMA: "qma",
  EXIT_STRATEGY: "exit_strategy",
});

const REQUIRED_KEYS_BY_AGENT = Object.freeze({
  [AGENT_TYPES.COMPS]: ["propertyId", "userId", "customerId"],
  [AGENT_TYPES.QMA]: ["propertyId", "userId", "customerId", "compsAnalysis"],
  [AGENT_TYPES.EXIT_STRATEGY]: ["propertyId", "userId", "customerId"],
});

const OPTIONAL_PAYLOAD_KEYS = Object.freeze([
  ["model_name", "modelName"],
  ["comps", "comps"],
  ["comps_analysis", "compsAnalysis"],
  ["strategies", "strategies"],
  ["mode", "mode"],
]);

const hasValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

function assertRequiredFields(agent, payload) {
  const requiredKeys = REQUIRED_KEYS_BY_AGENT[agent];
  if (!requiredKeys) {
    throw new Error(`Unsupported agent value: ${agent}`);
  }

  for (const key of requiredKeys) {
    if (!hasValue(payload[key])) {
      throw new Error(`Missing required invokeAgent field: ${key}`);
    }
  }
}

/**
 * @param {Object} params
 * @param {string} params.agent
 * @param {string} params.propertyId
 * @param {string} params.userId
 * @param {string} params.customerId
 * @param {string} [params.modelName]
 * @param {Array<string>|null} [params.comps]
 * @param {string|null} [params.compsAnalysis]
 * @param {Array<string>} [params.strategies]
 * @param {"full"|"rerun"} [params.mode] - comps only; "rerun" re-scores the comps already on the property
 * @returns {Object}
 */
export function invokeAgent({ agent, propertyId, userId, customerId, ...payload }) {
  const normalizedAgent = typeof agent === "string" ? agent.trim().toLowerCase() : "";
  assertRequiredFields(normalizedAgent, { propertyId, userId, customerId, ...payload });

  const variables = {
    agent: normalizedAgent,
    property_id: propertyId,
    user_id: userId,
    customer: customerId,
  };

  for (const [variableKey, payloadKey] of OPTIONAL_PAYLOAD_KEYS) {
    const value = payload[payloadKey];
    if (hasValue(value)) {
      variables[variableKey] = value;
    }
  }

  return variables;
}

/**
 * @param {Object} payload
 * @returns {Object}
 */
export function invokeCompsAgent(payload) {
  return invokeAgent({ agent: AGENT_TYPES.COMPS, ...payload });
}

/**
 * @param {Object} payload
 * @returns {Object}
 */
export function invokeQmaAutomation(payload) {
  return invokeAgent({ agent: AGENT_TYPES.QMA, ...payload });
}

/**
 * @param {Object} payload
 * @returns {Object}
 */
export function invokeExitStrategy(payload) {
  return invokeAgent({ agent: AGENT_TYPES.EXIT_STRATEGY, ...payload });
}
